import React, { useRef } from 'react';
import { formatTime } from '../../lib/formatDate';
import gafferIcon from '../../assets/gaffer-head-icon.png';
import { ShareButtons } from '../../components/ShareInvite';
import { joinLinkFor } from '../../lib/joinLink';
import { drawPostImage } from '../../lib/postImage';

/**
 * One message.
 *
 * Two shapes, and the difference between them is the hierarchy: a player's line is a
 * row — avatar, handle, what they said — with no card around it, because a two-word
 * joke in a bordered panel reads as an announcement. The Gaffer keeps the panel. He
 * is the one voice here that must never be mistaken for another member, and being
 * visually louder than everyone else is how that is achieved.
 *
 * The Gaffer's posts still render as two stacked pieces and never as one blob of
 * text: a fixed facts card built from `payload`, and his line beside it. That
 * separation is the whole point — a player scanning for a deadline finds it in the
 * same place, in the same words, every time, and never has to read past a joke to
 * get it. `STRAIGHT` and `NOTICE` posts carry no line at all; the server refuses to
 * attach one, so there is nothing to render.
 *
 * The facts card has two shapes now that the automation writes one. A flat object is
 * still rendered as label/value rows, which is what hand-written house posts carry. An
 * automated post carries `{ facts, breakdown, link, image }` instead: the same label/value rows,
 * a round's elimination table, and the single place a post may send a reader. The link is
 * a callback rather than an `<a href>` for the reason spelled out on `Mentioned` below —
 * this app has no router, so a URL would be a full page load to somewhere that does not
 * exist. A post whose host gave it no `onNavigate` simply renders no button.
 *
 * `NOTICE` goes further and loses him entirely — no face, no name. The register's own
 * definition is what decides this: `STRAIGHT` is "still him, no jokes" and keeps the
 * avatar, where `NOTICE` is "out of character entirely… read by lawyers and
 * regulators". A legal or safeguarding notice wearing a cartoon dog's face is the
 * thing that register exists to prevent.
 */
/**
 * `share` (backlog item 90) is where a shared Gaffer post should send people: `{ name, code }`,
 * with `code` the join code only when joining would actually work - a league, a game still open
 * or just rolled over - and null otherwise, when the link is just the site. Absent, no button.
 */
function BanterPost(props) {
  return props.post.fromHouse ? <GafferPost {...props} /> : <PlayerPost post={props.post} />;
}

function PlayerPost({ post }) {
  return (
    <article className="board-row">
      {/* An initial in the poster's own colour. Cheap, and it turns a wall of
          identical grey rows into something you can scan for a name. */}
      <span className="board-avatar" style={avatarStyle(post.authorHandle)} aria-hidden="true">
        {initial(post.authorHandle)}
      </span>
      <div className="board-row-main">
        <div className="board-row-head">
          <span className="board-row-author">{post.authorHandle}</span>
          <span className="board-row-time">{formatTime(post.createdAt)}</span>
        </div>
        <p className="board-row-body">
          <Mentioned body={post.body} handle={post.targetHandle} id={post.targetPlayerId} />
        </p>
      </div>
    </article>
  );
}

function GafferPost({ post, onNavigate, share }) {
  // The tile's own pictures, so the shared image is drawn from what is on screen (item 92).
  const headRef = useRef(null);
  const pictureRef = useRef(null);
  const payload = parsePayload(post.payload);
  const { facts, breakdown, link, image } = readPayload(payload);
  const serious = post.register === 'STRAIGHT' || post.register === 'NOTICE';
  const outOfCharacter = post.register === 'NOTICE';
  const label = labelFor(post.postType);

  return (
    <article className={`board-post gaffer${serious ? ' serious' : ''}`}>
      <div className="board-post-head">
        {outOfCharacter ? (
          <span className="board-post-author gaffer-name">Notice</span>
        ) : (
          <>
            <img ref={headRef} className="board-gaffer-avatar" src={gafferIcon} alt="" aria-hidden="true" />
            <span className="board-post-author gaffer-name">The Gaffer</span>
          </>
        )}
        {label && <span className="board-post-badge">{label}</span>}
        <span className="board-row-time">{formatTime(post.createdAt)}</span>
      </div>

      {/* The facts. Fixed shape, plain words, identical every time. */}
      {facts && (
        <dl className="board-post-payload">
          {Object.entries(facts).map(([key, value]) => (
            <div key={key} className="board-payload-row">
              <dt>{humanise(key)}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* The round's ledger. A table rather than more label/value rows because the
          question it answers — "who did for whom" — is read across, not down, and the
          names are the whole point of it. */}
      {breakdown && <Breakdown rows={breakdown} />}

      {/* His picture, chosen with his line on the server (backlog item 64): the key names one
          of the backend's own pictures, so there is a single library and no copy here. */}
      {image && (
        <img
          ref={pictureRef}
          className="board-post-image"
          src={`/api/gaffer-images/${image}`}
          alt="The Gaffer"
          loading="lazy"
        />
      )}

      {/* His line. Always visually secondary to the facts above it. */}
      {post.body && (
        <p className="board-post-flavour">
          <Mentioned body={post.body} handle={post.targetHandle} id={post.targetPlayerId} />
        </p>
      )}

      {/* The one place a post may send you. Rendered last so it reads as what to do
          after the facts, and dropped entirely when nobody is listening for it. */}
      {link && onNavigate && (
        <button
          type="button"
          className="board-post-link"
          onClick={() => onNavigate(link.target)}
        >
          {link.label}
        </button>
      )}

      {/* Out to a group chat. Never on a notice: that register is for the lawyers, not for
          WhatsApp, and passing one around out of context is the misreading it exists to stop. */}
      {share && !outOfCharacter && (
        <ShareButtons
          className="board-post-share"
          title="The Gaffer"
          label="this update"
          message={shareTextFor(post, share)}
          prepareFile={() => drawPostImage(
            { name: share.name, body: post.body, facts: facts && humaniseKeys(facts), breakdown },
            { head: headRef.current, picture: pictureRef.current }
          )}
        />
      )}
    </article>
  );
}

/**
 * Which team accounted for whom, in one round.
 *
 * Sorted server-side, most damage first, and deliberately not re-sorted here — the same
 * order has to hold between the post and anything else reading the same figures.
 *
 * Rows with no casualties are still shown. Leaving them out would turn a ledger into a
 * list of villains, and "everyone who backed Brentford is through" is the half of the
 * round a surviving player actually wants.
 */
function Breakdown({ rows }) {
  return (
    <div className="board-breakdown">
      <div className="board-breakdown-head" aria-hidden="true">
        <span>Team</span>
        <span>Backed</span>
        <span>Out</span>
      </div>
      {rows.map((row, i) => (
        <div key={`${row.team}-${i}`} className={`board-breakdown-row${row.out > 0 ? ' damage' : ''}`}>
          <div className="board-breakdown-line">
            <span className="board-breakdown-team">{row.team}</span>
            <span className="board-breakdown-count">{row.picks}</span>
            <span className="board-breakdown-count">
              {row.out}
              {row.share ? <em> ({row.share})</em> : null}
            </span>
          </div>
          {row.players && row.players.length > 0 && (
            <p className="board-breakdown-names out">Out: {row.players.join(', ')}</p>
          )}
          {row.survivors && row.survivors.length > 0 && (
            <p className="board-breakdown-names through">Through: {row.survivors.join(', ')}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A player named in a line, rendered as a mention rather than as prose.
 *
 * The body is deliberately frozen at the moment it was posted — editing a phrase must
 * never rewrite what somebody said last month — so the handle is found in the stored
 * text rather than substituted into it.
 *
 * <strong>Not a link, on purpose.</strong> This app has no router: it switches on a
 * `currentPage` state value, so an `<a href>` would be a full page load to a URL that
 * does not exist, dropping the session view and landing on nothing. There is also no
 * per-player page to arrive at yet. The id is carried on the element regardless,
 * because it is the durable identifier — the handle in the text is a snapshot of what
 * they were called that day, and the day somebody builds a player page, the
 * destination is already here.
 *
 * Underlined as well as coloured, so a mention is still a mention to anyone who
 * cannot pick the purple out of the sentence.
 */
export function Mentioned({ body, handle, id }) {
  if (!body) return null;
  if (!handle) return <>{body}</>;

  const parts = String(body).split(new RegExp(`(${escapeRegExp(handle)})`, 'g'));
  return (
    <>
      {parts.map((part, i) =>
        part === handle ? (
          <span key={i} className="board-mention" data-player-id={id || undefined}>
            @{part}
          </span>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

/**
 * A handle always gets the same colour, on every device and every reload, because the
 * hue is derived from the handle rather than assigned. Fixed saturation and lightness
 * so every hue lands at the same weight and none of them come out unreadable.
 */
function avatarStyle(handle) {
  const name = handle || '?';
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 360;
  }
  return { background: `hsl(${hash}, 62%, 45%)` };
}

function initial(handle) {
  return (handle || '?').trim().charAt(0).toUpperCase() || '?';
}

// A post whose payload will not parse still has to render — the facts card is
// dropped rather than taking the whole board down with it.
/**
 * A Gaffer post as plain text for a group chat (backlog item 90): his words, then the facts card
 * as "Label: value" lines and the round table as one line per team, then the link last so
 * WhatsApp turns it into a preview card. Plain text because a chat is plain text - the same
 * figures, in the same order the board shows them, and nothing the board does not show.
 */
export function shareTextFor(post, { name, code } = {}) {
  const { facts, breakdown } = readPayload(parsePayload(post.payload));
  const lines = [name ? `The Gaffer, in "${name}":` : 'The Gaffer:'];
  if (post.body) lines.push(post.body);
  if (facts) {
    Object.entries(facts).forEach(([key, value]) => lines.push(`${humanise(key)}: ${value}`));
  }
  if (breakdown) {
    breakdown.forEach((row) => lines.push(`${row.team}: ${row.picks} backed, ${row.out} out`));
  }
  lines.push(code ? `Join us on Gaffer Picks: ${joinLinkFor(code)}` : `Gaffer Picks: ${window.location.origin}`);
  return lines.join('\n');
}

function parsePayload(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The two payload shapes, told apart by what is in them.
 *
 * `image` is the fourth automated key (backlog item 64): a picture's name in the backend's
 * library, accepted only in that name's shape so a payload can never steer the `src` anywhere
 * else.
 *
 * A hand-written house post carries a flat label/value object and predates the
 * automation; an automated one carries the three named keys. Sniffing rather than
 * versioning, because there is no version on the stored JSON to read and every post
 * already written is the flat kind — a flag would have to be back-filled onto rows whose
 * whole purpose is never to be rewritten.
 */
function readPayload(payload) {
  if (!payload) return {};
  const structured = payload.facts || payload.breakdown || payload.link || payload.image;
  if (!structured) {
    return { facts: payload };
  }
  return {
    facts: isPlainObject(payload.facts) ? payload.facts : null,
    breakdown: Array.isArray(payload.breakdown) && payload.breakdown.length > 0
      ? payload.breakdown
      : null,
    link: payload.link && payload.link.label && payload.link.target ? payload.link : null,
    image: typeof payload.image === 'string' && IMAGE_KEY.test(payload.image) ? payload.image : null,
  };
}

const IMAGE_KEY = /^[a-z0-9-]{1,60}$/;

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).length > 0;
}

function humaniseKeys(facts) {
  return Object.fromEntries(Object.entries(facts).map(([key, value]) => [humanise(key), value]));
}

function humanise(key) {
  const spaced = key.replace(/([A-Z])/g, ' $1').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).trim();
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Plain banter carries no badge. Everything else does, because the badge is how a
// player tells "he is joking" from "this changes your deadline" at a glance.
function labelFor(postType) {
  switch (postType) {
    case 'RESCHEDULE':
      return 'Reschedule';
    case 'RESULT_NOTE':
      return 'Result';
    case 'ANNOUNCEMENT':
      return 'Announcement';
    default:
      return null;
  }
}

export default BanterPost;
